import type { FixCheck, FixCheckConfirm } from '@testflow/shared';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MyDefectDetails } from './my-defect-details.js';

// =================================================================================================
// #194 (Nachtrag 14.10.): Detail zur gewählten Karte „Fehler prüfen" — steht in „Meine Tests" rechts,
// dort, wo sonst die Ausführung des Testfalls steht. Oben Überschrift und Aktionsleiste („Behoben";
// „Nicht behoben" klappt das Pflichtfeld „Was ist noch falsch?" und „Zurück in Behebung" auf — Logik
// aus der bisherigen Karte), darunter der Defect-Körper mit Verlauf und Kommentarfeld
// (MyDefectDetails). Ein Klick auf den Testfall dort öffnet ihn in der Seitenleiste der Seite.
//
// Die Seite remountet das Detail je Defect (`key`): ein halb geschriebener Grund geht beim
// Auswahlwechsel bewusst verloren, statt an einen anderen Defect zu wandern.
// =================================================================================================

export function FixCheckDetail({
  projectId,
  check,
  pending,
  onConfirm,
  onOpenTestCase,
  canViewDefect,
}: {
  projectId: string;
  check: FixCheck;
  // Die Bestätigung läuft (bis das Neuladen die Karte entfernt hat) — kein zweites Absenden.
  pending: boolean;
  onConfirm: (body: FixCheckConfirm) => void;
  onOpenTestCase: (testCaseId: string, trigger: HTMLElement) => void;
  // Fix-Runde 1: mit defect.edit (Regel der Seite, wie früher der Link an der Karten-ID) ein Link ins
  // Defect-Board — Testmanager, die selbst gemeldet haben, wollen dort weiterarbeiten.
  canViewDefect: boolean;
}) {
  const { t } = useTranslation();
  const [reasonOpen, setReasonOpen] = useState(false);
  const [comment, setComment] = useState('');
  const commentId = useId();
  // Leerraum ist keine Begründung (Server: trim().min(1), FixCheckConfirmSchema).
  const trimmed = comment.trim();

  return (
    <div className="space-y-4 p-6">
      <div className="space-y-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 className="text-[11px] font-bold uppercase tracking-wide text-status-fail">
            {t('fixCheck.detailTitle', { id: check.human_id })}
          </h2>
          {canViewDefect && (
            <Link
              to={`/defects/${check.defect_id}`}
              className="text-xs text-brand-accent hover:underline"
            >
              {t('fixCheck.openInBoard')}
            </Link>
          )}
        </div>
        <p className="break-words text-lg font-semibold text-text-primary">{check.title}</p>
        {/* Vertretung (B4): wer hier prüft, prüft für den abwesenden Melder. */}
        {check.substituted_for && (
          <span className="inline-block rounded-chip bg-[var(--tint-blue)] px-2 py-0.5 text-[11px] text-text-body">
            {t('exec.substitutedFor', { name: check.substituted_for.name })}
          </span>
        )}
      </div>

      {/* Aktionsleiste über dem Defect-Körper: auf 1280×800 ohne Scrollen erreichbar, auch wenn der
          Verlauf lang ist. */}
      <div className="overflow-hidden rounded-card border border-status-fail">
        <div className="flex flex-wrap gap-2 px-3 py-2">
          <button
            type="button"
            disabled={pending}
            onClick={() => onConfirm({ fixed: true })}
            className="rounded-btn bg-status-pass px-4 py-1.5 text-sm font-bold text-white disabled:opacity-50"
          >
            {t('fixCheck.fixed')}
          </button>
          <button
            type="button"
            disabled={pending}
            aria-expanded={reasonOpen}
            onClick={() => setReasonOpen((o) => !o)}
            className={`rounded-btn border border-status-fail px-4 py-1.5 text-sm font-bold text-status-fail disabled:opacity-50 ${
              reasonOpen ? 'bg-status-fail/10' : 'bg-bg-surface'
            }`}
          >
            {t('fixCheck.notFixed')}
          </button>
        </div>
        {reasonOpen && (
          <div className="flex flex-col gap-2 border-t border-dashed border-status-fail/40 bg-status-fail/5 px-3 py-2">
            <label htmlFor={commentId} className="text-xs text-text-muted">
              {t('fixCheck.commentLabel')}
            </label>
            <textarea
              id={commentId}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={2000}
              className="w-full resize-y rounded-btn border border-status-fail bg-bg-surface px-2 py-1 text-sm text-text-body"
            />
            <button
              type="button"
              disabled={pending || trimmed === ''}
              onClick={() => onConfirm({ fixed: false, comment: trimmed })}
              className="self-end rounded-btn bg-status-fail px-4 py-1.5 text-sm font-bold text-white disabled:opacity-50"
            >
              {t('fixCheck.sendBack')}
            </button>
          </div>
        )}
      </div>

      <MyDefectDetails
        projectId={projectId}
        defectId={check.defect_id}
        onOpenTestCase={onOpenTestCase}
        mode="fixCheck"
      />
    </div>
  );
}
